<template>
  <rest-page :fetch-endpoint="fetchEndpoint">
    <page-header-new
      :title="$t('ProjectSettings_EngagementCategories_Title')"
      :breadcrumbs="breadcrumbs"
    >
      <template #actions>
        <save-button :click="save" :disabled="readOnly" />
      </template>
    </page-header-new>
    <page-content>
      <rest-form
        ref="form"
        :save-endpoint="saveEndpoint"
        :after-save="afterSave"
        :readonly="readOnly"
        :model="model"
        :rules="rules"
        v-if="model"
      >
        <engagement-categories-settings-section
          :model="model"
          :is-national-exclusion-available="isNationalExclusionAvailable"
          :is-betsson-enabled="isBetssonEnabled"
          :is-oasis-enabled="isOasisEnabled"
          :read-only="readOnly"
        />

        <engagement-categories-exclusion-section
          :category-model="model"
          @updateExclusionEnabled="updateExclusionEnabled"
          @clearExclusionModel="clearExclusionModel"
          @validateExclusionAmount="validateExclusionAmount"
          :read-only="readOnly"
        />

        <engagement-categories-frequency-capping-section
          :model="model"
          @frequencyCappingChange="updateLimitsEnabled"
          :read-only="readOnly"
          :limits-enabled="limits_enabled"
          @validateFields="validateFields"
          @validateCooldown="validateCooldown"
        />
      </rest-form>
    </page-content>
  </rest-page>
</template>

<script>
import RestPage from '@/common/components/rest/RestPage'
import RestForm from '@/common/components/rest/RestForm'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import { nationalExclusionTypes } from '@/common/enum/CategoryEnums'
import SaveButton from '@/common/components/form/SaveButton'
import { mapGetters } from 'vuex'
import { $hasFeature } from '@/core/utils/VueAcl'
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import PageContent from '@/common/components/page/PageContent'
import EngagementCategoriesSettingsSection from '@/modules/campaigns/components/EngagementCategoriesSettingsSection.vue'
import EngagementCategoriesFrequencyCappingSection from '@/modules/campaigns/components/EngagementCategoriesFrequencyCappingSection.vue'
import EngagementCategoriesExclusionSection from '@/modules/campaigns/components/EngagementCategoriesExclusionSection.vue'

export default {
  name: 'EngagementCategoryFormPage',

  props: {
    categoryId: {
      default: null,
      type: Number
    },
    afterSaveCategory: {
      type: Function
    },
    index: {
      type: Number
    }
  },

  components: {
    EngagementCategoriesExclusionSection,
    EngagementCategoriesFrequencyCappingSection,
    EngagementCategoriesSettingsSection,
    PageContent,
    PageHeaderNew,
    SaveButton,
    RestForm,
    RestPage
  },

  mounted() {
    this.init()
  },

  data() {
    return {
      model: null,
      limits_enabled: false,
      exclusion_enabled: 0,
      category_id: null,
      nationalExclusionTypes
    }
  },

  computed: {
    ...mapGetters(['isBetssonAvailable', 'isOasisAvailable']),
    breadcrumbs() {
      return [
        { label: this.$t('Campaigns_Breadcrumb_Campaigns') },
        {
          label: this.$t('ProjectSettings_EngagementCategories_Title'),
          route: projectRoute({ name: 'EngagementCategoriesTab' })
        },
        { label: this.model ? this.model.title : '' }
      ]
    },
    isNationalExclusionAvailable() {
      return this.isBetssonEnabled || this.isOasisEnabled
    },
    isBetssonEnabled() {
      return this.isBetssonAvailable && !!$hasFeature('betssonIntegration')
    },
    isOasisEnabled() {
      return this.isOasisAvailable && !!$hasFeature('oasisIntegration')
    },
    rules() {
      let validateLimit = (rule, value, callback) => {
        if (value === '' || value === null) {
          callback()
        } else if (/^[0]+$/.test(value)) {
          callback(
            new Error(this.$t('Engagement_Category_Error_More_Than_Zero'))
          )
        } else if (!/^[0-9]+$/.test(value)) {
          callback(
            new Error(this.$t('Engagement_Category_Error_Must_Be_A_Number'))
          )
        } else {
          callback()
        }
      }
      let validateDayLimit = (rule, value, callback) => {
        if (value === '' || value === null) {
          callback()
        } else if (/^[0]+$/.test(value)) {
          callback(
            new Error(this.$t('Engagement_Category_Error_More_Than_Zero'))
          )
        } else if (!/^[0-9]+$/.test(value)) {
          callback(
            new Error(this.$t('Engagement_Category_Error_Must_Be_A_Number'))
          )
        } else if (
          this.model.max_per_week &&
          parseInt(value) > parseInt(this.model.max_per_week)
        ) {
          callback(
            new Error(this.$t('Engagement_Category_Error_Less_Than_Week'))
          )
        } else if (
          this.model.max_per_month &&
          parseInt(value) > parseInt(this.model.max_per_month)
        ) {
          callback(
            new Error(this.$t('Engagement_Category_Error_Less_Than_Month'))
          )
        } else {
          callback()
        }
      }
      let validateWeekLimit = (rule, value, callback) => {
        if (value === '' || value === null) {
          callback()
        } else if (/^[0]+$/.test(value)) {
          callback(
            new Error(this.$t('Engagement_Category_Error_More_Than_Zero'))
          )
        } else if (!/^[0-9]+$/.test(value)) {
          callback(
            new Error(this.$t('Engagement_Category_Error_Must_Be_A_Number'))
          )
        } else if (
          this.model.max_per_day &&
          parseInt(value) < parseInt(this.model.max_per_day)
        ) {
          callback(
            new Error(this.$t('Engagement_Category_Error_Greater_Than_Days'))
          )
        } else if (
          this.model.max_per_month &&
          parseInt(value) > parseInt(this.model.max_per_month)
        ) {
          callback(
            new Error(this.$t('Engagement_Category_Error_Less_Than_Month'))
          )
        } else {
          callback()
        }
      }
      let validateMonthLimit = (rule, value, callback) => {
        if (value === '' || value === null) {
          callback()
        } else if (/^[0]+$/.test(value)) {
          callback(
            new Error(this.$t('Engagement_Category_Error_More_Than_Zero'))
          )
        } else if (!/^[0-9]+$/.test(value)) {
          callback(
            new Error(this.$t('Engagement_Category_Error_Must_Be_A_Number'))
          )
        } else if (
          this.model.max_per_day &&
          parseInt(value) < parseInt(this.model.max_per_day)
        ) {
          callback(
            new Error(this.$t('Engagement_Category_Error_Greater_Than_Days'))
          )
        } else if (
          this.model.max_per_week &&
          parseInt(value) < parseInt(this.model.max_per_week)
        ) {
          callback(
            new Error(this.$t('Engagement_Category_Error_Greater_Than_Weeks'))
          )
        } else {
          callback()
        }
      }

      let validateAtLeastOntLimit = (rule, value, callback) => {
        if (
          this.limits_enabled &&
          !this.model.max_per_day &&
          !this.model.max_per_week &&
          !this.model.max_per_month &&
          !this.model.max_lifetime_sends &&
          !this.model.cooldown_amount
        ) {
          callback(
            new Error(this.$t('Engagement_Category_AtLeastOneLimitRequired'))
          )
        } else {
          callback()
        }
      }

      let validateExclusionAmount = (rule, value, callback) => {
        const yearLimits = {
          minutes: 525601,
          hours: 8760,
          days: 365,
          weeks: 52,
          months: 12
        }

        if (this.exclusion_enabled) {
          if (value === '' || value === null) {
            callback(
              new Error(
                this.$t('Engagement_Category_Exclusion_Amount_Required')
              )
            )
          } else if (/^[0]+$/.test(value)) {
            callback(
              new Error(this.$t('Engagement_Category_Error_More_Than_Zero'))
            )
          } else if (!/^[0-9]+$/.test(value)) {
            callback(
              new Error(this.$t('Engagement_Category_Error_Must_Be_A_Number'))
            )
          } else if (value > yearLimits[this.model.exclusion_measure]) {
            callback(
              new Error(
                this.$t('Engagement_Category_Error_Twelve_Months_Maximum')
              )
            )
          } else {
            callback()
          }
        }
      }

      return {
        title: [
          {
            required: true,
            message: this.$t('Engagement_Category_Title_Required')
          }
        ],
        limits_enabled: [
          {
            validator: validateAtLeastOntLimit,
            trigger: 'submit'
          }
        ],
        max_per_day: [{ validator: validateDayLimit }],
        max_per_week: [{ validator: validateWeekLimit }],
        max_per_month: [{ validator: validateMonthLimit }],
        max_lifetime_sends: [{ validator: validateLimit }],
        cooldown_amount: [{ validator: validateLimit }],
        cooldown_measure: [
          {
            required: !!this.model.cooldown_amount,
            message: this.$t('Engagement_Category_Cooldown_Measure_Required')
          }
        ],
        exclusion_category_ids: [
          {
            required:
              this.exclusion_enabled &&
              !this.model.global_exclusion &&
              this.model.exclusion_category_ids.length === 0,
            message: this.$t('Engagement_Category_Exclusion_Category_Required'),
            trigger: 'submit'
          }
        ],
        exclusion_amount: [
          {
            validator: validateExclusionAmount,
            trigger: 'submit'
          }
        ],
        exclusion_measure: [
          {
            required: this.exclusion_enabled,
            message: this.$t('Engagement_Category_Exclusion_Measure_Required'),
            trigger: 'submit'
          }
        ]
      }
    },

    fetchEndpoint() {
      if (this.$route.query.id) {
        return projectEndpoint({
          method: 'get',
          url: `categories/${this.$route.query.id}`,
          params: {
            expand: 'exclusion_category_ids'
          },
          handler: data => {
            this.limits_enabled = !!(
              data.max_per_day ||
              data.max_per_week ||
              data.max_per_month ||
              data.max_lifetime_sends ||
              data.cooldown_amount
            )

            this.model = data
          }
        })
      } else {
        return null
      }
    },

    saveEndpoint() {
      if (this.category_id) {
        return projectEndpoint({
          method: 'patch',
          url: `categories/${this.category_id}`
        })
      } else {
        return projectEndpoint({
          method: 'post',
          url: 'categories'
        })
      }
    },

    readOnly() {
      if (this.category_id) {
        return !this.$can('updateProjectItem')
      }
      return !this.$can('createProjectItem')
    }
  },

  methods: {
    init() {
      if (this.$route.query.id) {
        this.category_id = this.$route.query.id
      } else {
        this.model = {
          title: null,
          ignore_subscription: 0,
          collapse: 0,
          max_per_day: null,
          max_per_week: null,
          max_per_month: null,
          max_lifetime_sends: null,
          cooldown_amount: null,
          cooldown_measure: null,
          apply_across_channels: 0,
          use_national_exclusion: null,
          national_exclusion_check: null,
          global_exclusion: null,
          exclusion_amount: null,
          exclusion_measure: null,
          exclusion_category_ids: []
        }
      }
    },

    afterSave(data, finishLoading) {
      this.category_id = data.id
      finishLoading(this.afterSaveCategory)
      if (this.afterSaveCategory) {
        if (this.index !== null) {
          this.afterSaveCategory(this.index)
        }
      }
      this.$router.push(
        projectRoute({
          name: 'EngagementCategoriesTab'
        })
      )
    },

    save() {
      this.$refs['form'].save()
    },

    updateLimitsEnabled(value) {
      this.limits_enabled = value
    },

    updateExclusionEnabled(value) {
      this.exclusion_enabled = value
    },

    validateFields() {
      this.$refs.form.validateField([
        'max_per_day',
        'max_per_week',
        'max_per_month',
        'max_lifetime_sends'
      ])
    },

    validateCooldown() {
      this.$refs.form.validateField(['cooldown_amount'])
    },

    validateExclusionAmount() {
      this.$refs.form.validateField(['exclusion_amount'])
    },

    clearExclusionModel() {
      this.model.exclusion_category_ids = []
      this.model.exclusion_measure = null
      this.model.exclusion_amount = null
      this.model.global_exclusion = 0
    }
  },

  watch: {
    'model.use_national_exclusion': {
      handler(newVal, oldVal) {
        if (oldVal === 1 && newVal === 0) {
          this.$set(this.model, 'national_exclusion_check', null)
        }

        if (oldVal === 0 && newVal === 1) {
          if (this.isBetssonEnabled) {
            this.$set(
              this.model,
              'national_exclusion_check',
              nationalExclusionTypes.marketing
            )
          } else if (this.isOasisEnabled) {
            this.$set(
              this.model,
              'national_exclusion_check',
              nationalExclusionTypes.marketing_oasis
            )
          }
        }
      }
    },
    limits_enabled: {
      handler(val) {
        if (!val) {
          this.model.max_per_day = null
          this.model.max_per_week = null
          this.model.max_per_month = null
          this.model.max_lifetime_sends = null
          this.model.cooldown_amount = null
          this.model.cooldown_measure = null
          this.model.apply_across_channels = 0
        }
      }
    }
  }
}
</script>

<style scoped>
.save-button {
  float: right;
}
>>> .el-switch {
  margin-top: 8px;
}
>>> .el-input {
  width: 100%;
}
>>> .el-form-item__label {
  line-height: unset !important;
}
>>> .el-form-item__error {
  width: 168px;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaigns/components/EngagementCategoriesFormPage.vue